'use client'

import { forwardRef, useId } from 'react'

import { LuCheck } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import type { InputHTMLAttributes, ReactNode } from 'react'

type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: ReactNode
  error?: string
  classNames?: {
    container?: string
    input?: string
    label?: string
    /** 라벨 글자 — 주면 기본 글자 스타일을 대신한다 */
    text?: string
  }
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, classNames, id, ...props }, ref) => {
    const generatedId = useId()
    const inputId = id || props.name || generatedId
    const errorId = error ? `${inputId}-error` : undefined

    return (
      <div className={cn(classNames?.container)}>
        <label
          htmlFor={inputId}
          className={cn(
            'inline-flex cursor-pointer select-none items-center gap-2.5',
            props.disabled && 'cursor-not-allowed opacity-50',
            classNames?.label
          )}
        >
          <span className="relative inline-flex shrink-0 items-center justify-center">
            <input
              ref={ref}
              type="checkbox"
              id={inputId}
              className="peer sr-only"
              aria-invalid={Boolean(error)}
              aria-describedby={errorId}
              {...props}
            />
            <span
              className={cn(
                'size-5 rounded-control border-[1.5px] border-description/40 bg-background',
                'transition-[background-color,border-color] duration-200',
                'peer-checked:border-point peer-checked:bg-point',
                'peer-focus-visible:border-point',
                classNames?.input
              )}
            />
            <LuCheck
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute size-3.5 scale-75 text-inverse opacity-0',
                'transition-[opacity,transform] duration-200',
                'peer-checked:scale-100 peer-checked:opacity-100'
              )}
            />
          </span>
          {label && (
            <span className={classNames?.text ?? 'text-c14 text-foreground'}>
              {label}
            </span>
          )}
        </label>
        {error && (
          <p
            id={errorId}
            role="alert"
            className="mt-2 text-c12 leading-5 text-error"
          >
            {error}
          </p>
        )}
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'
